import apiClient from '@/lib/axios'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface MenuItem {
  _id?: string // MongoDB ObjectId
  id: string // Alias for _id for compatibility
  branchId?: string
  restaurant_id?: string
  name: string
  name_ar?: string
  name_en?: string
  nameAr?: string // Legacy field
  description?: string
  price: number
  img_url?: string
  imageUrl?: string // Legacy field
  // Category can be either a string (ID) or populated object from MongoDB
  category?: string | {
    _id: string
    name: string
    synonyms?: string[]
  }
  ingredients?: string[]
  ingredients_ar?: string[]
  allergens?: string[]
  allergens_ar?: string[]
  dietary?: string[]
  nutrition?: Record<string, any>
  prep_time_minutes?: number
  prep_time_ar?: string
  tags?: string[]
  tags_ar?: string[]
  search_keywords?: string[]
  isAvailable: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateMenuItemInput {
  name: string
  name_ar?: string
  name_en?: string
  nameAr?: string // Legacy
  description?: string
  price: number
  img_url?: string
  imageUrl?: string // Legacy
  category?: string
  ingredients?: string[]
  ingredients_ar?: string[]
  allergens?: string[]
  allergens_ar?: string[]
  dietary?: string[]
  nutrition?: Record<string, any>
  prep_time_minutes?: number
  prep_time_ar?: string
  tags?: string[]
  tags_ar?: string[]
  search_keywords?: string[]
  isAvailable?: boolean
}

export interface UpdateMenuItemInput {
  name?: string
  name_ar?: string
  name_en?: string
  nameAr?: string // Legacy
  description?: string
  price?: number
  img_url?: string
  imageUrl?: string // Legacy
  category?: string
  ingredients?: string[]
  ingredients_ar?: string[]
  allergens?: string[]
  allergens_ar?: string[]
  dietary?: string[]
  nutrition?: Record<string, any>
  prep_time_minutes?: number
  prep_time_ar?: string
  tags?: string[]
  tags_ar?: string[]
  search_keywords?: string[]
  isAvailable?: boolean
}

export function useMenuItems(selectedBranchId?: string | null, selectedRestaurantId?: string | null) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  
  console.log('[useMenuItems] Hook called with selectedBranchId:', selectedBranchId, 'restaurantId:', selectedRestaurantId)

  // Query for fetching menu items list (paginated)
  // Note: This query is not currently used by MenuItems.tsx (which uses manual fetchMenuItems),
  // but it's kept for potential future use. It includes branchId for consistency.
  const menuItemsQuery = useQuery<{ data: MenuItem[]; meta: { page: number; limit: number; total: number; hasMore: boolean } }>({
    queryKey: ['menu-items', selectedBranchId, selectedRestaurantId],
    queryFn: async () => {
      if (!selectedBranchId) {
        throw new Error('BranchId is required to fetch menu items')
      }
      if (!selectedRestaurantId) {
        throw new Error('RestaurantId is required to fetch menu items')
      }
      const token = await getToken()
      const params = new URLSearchParams()
      if (selectedBranchId) {
        params.append('branchId', selectedBranchId)
      }
      if (selectedRestaurantId) {
        params.append('restaurantId', selectedRestaurantId)
      }
      console.log('[useMenuItems] Query fetch request:', {
        selectedBranchId,
        selectedRestaurantId,
        url: `/menu-items?${params.toString()}`,
      })
      const response = await apiClient.get(`/menu-items?${params.toString()}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'X-Branch-ID': selectedBranchId,
          'X-Restaurant-ID': selectedRestaurantId,
        },
      })
      return response.data
    },
    enabled: !!selectedBranchId && !!selectedRestaurantId, // Only run query if both branchId and restaurantId are available
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
    refetchOnWindowFocus: true,
    retry: 2,
  })

  // Mutation for creating a menu item
  const createMutation = useMutation({
    mutationFn: async (data: CreateMenuItemInput) => {
      if (!selectedBranchId) {
        throw new Error('BranchId is required to create menu item')
      }
      if (!selectedRestaurantId) {
        throw new Error('RestaurantId is required to create menu item')
      }
      const token = await getToken()
      const params = new URLSearchParams()
      if (selectedBranchId) {
        params.append('branchId', selectedBranchId)
      }
      if (selectedRestaurantId) {
        params.append('restaurantId', selectedRestaurantId)
      }
      console.log('[useMenuItems] createMenuItem request:', {
        selectedBranchId,
        selectedRestaurantId,
        url: `/menu-items?${params.toString()}`,
      })
      const response = await apiClient.post<MenuItem>(
        `/menu-items?${params.toString()}`,
        data,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            'X-Branch-ID': selectedBranchId,
            'X-Restaurant-ID': selectedRestaurantId,
          },
        }
      )
      return response.data
    },
    onMutate: async (newMenuItem) => {
      await queryClient.cancelQueries({ queryKey: ['menu-items'] })
      const previousData = queryClient.getQueryData(['menu-items'])

      queryClient.setQueryData<{ data: MenuItem[]; meta: { total: number; page: number; limit: number } }>(['menu-items'], (old) => {
        if (!old) return old
        const optimisticMenuItem: MenuItem = {
          id: `temp-${Date.now()}`,
          branchId: '',
          name: newMenuItem.name,
          name_ar: newMenuItem.name_ar,
          nameAr: newMenuItem.nameAr,
          description: newMenuItem.description,
          price: newMenuItem.price,
          img_url: newMenuItem.img_url,
          imageUrl: newMenuItem.imageUrl,
          category: newMenuItem.category,
          ingredients: newMenuItem.ingredients,
          isAvailable: newMenuItem.isAvailable !== false,
          allergens: newMenuItem.allergens || [],
          tags: newMenuItem.tags || [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        return {
          ...old,
          data: [optimisticMenuItem, ...old.data],
        }
      })

      return { previousData }
    },
    onError: (_err, _newMenuItem, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['menu-items'], context.previousData)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-items'] })
    },
  })

  // Mutation for updating a menu item
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateMenuItemInput }) => {
      if (!selectedBranchId) {
        throw new Error('BranchId is required to update menu item')
      }
      if (!selectedRestaurantId) {
        throw new Error('RestaurantId is required to update menu item')
      }
      const token = await getToken()
      const params = new URLSearchParams()
      if (selectedBranchId) {
        params.append('branchId', selectedBranchId)
      }
      if (selectedRestaurantId) {
        params.append('restaurantId', selectedRestaurantId)
      }
      console.log('[useMenuItems] updateMenuItem request:', {
        id,
        selectedBranchId,
        selectedRestaurantId,
        url: `/menu-items/${id}?${params.toString()}`,
      })
      const response = await apiClient.patch<MenuItem>(
        `/menu-items/${id}?${params.toString()}`,
        data,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            'X-Branch-ID': selectedBranchId,
            'X-Restaurant-ID': selectedRestaurantId,
          },
        }
      )
      return response.data
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: ['menu-items'] })
      const previousData = queryClient.getQueryData(['menu-items'])

      queryClient.setQueryData<{ data: MenuItem[]; meta: { total: number; page: number; limit: number } }>(['menu-items'], (old) => {
        if (!old) return old
        return {
          ...old,
          data: old.data.map((item) =>
            item.id === id
              ? { ...item, ...data, updatedAt: new Date().toISOString() }
              : item
          ),
        }
      })

      return { previousData }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['menu-items'], context.previousData)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-items'] })
    },
  })

  // Mutation for deleting a menu item
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!selectedBranchId) {
        throw new Error('BranchId is required to delete menu item')
      }
      if (!selectedRestaurantId) {
        throw new Error('RestaurantId is required to delete menu item')
      }
      const token = await getToken()
      const params = new URLSearchParams()
      if (selectedBranchId) {
        params.append('branchId', selectedBranchId)
      }
      if (selectedRestaurantId) {
        params.append('restaurantId', selectedRestaurantId)
      }
      console.log('[useMenuItems] deleteMenuItem request:', {
        id,
        selectedBranchId,
        selectedRestaurantId,
        url: `/menu-items/${id}?${params.toString()}`,
      })
      await apiClient.delete(`/menu-items/${id}?${params.toString()}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'X-Branch-ID': selectedBranchId,
          'X-Restaurant-ID': selectedRestaurantId,
        },
      })
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['menu-items'] })
      const previousData = queryClient.getQueryData(['menu-items'])

      queryClient.setQueryData<{ data: MenuItem[]; meta: { total: number; page: number; limit: number } }>(['menu-items'], (old) => {
        if (!old) return old
        return {
          ...old,
          data: old.data.filter((item) => item.id !== id),
        }
      })

      return { previousData }
    },
    onError: (_err, _id, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['menu-items'], context.previousData)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-items'] })
    },
  })

  // Mutation for toggling availability
  const toggleAvailabilityMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!selectedBranchId) {
        throw new Error('BranchId is required to toggle availability')
      }
      if (!selectedRestaurantId) {
        throw new Error('RestaurantId is required to toggle availability')
      }
      const token = await getToken()
      const params = new URLSearchParams()
      if (selectedBranchId) {
        params.append('branchId', selectedBranchId)
      }
      if (selectedRestaurantId) {
        params.append('restaurantId', selectedRestaurantId)
      }
      console.log('[useMenuItems] toggleAvailability request:', {
        id,
        selectedBranchId,
        selectedRestaurantId,
        url: `/menu-items/${id}/availability?${params.toString()}`,
      })
      const response = await apiClient.patch<MenuItem>(
        `/menu-items/${id}/availability?${params.toString()}`,
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            'X-Branch-ID': selectedBranchId,
            'X-Restaurant-ID': selectedRestaurantId,
          },
        }
      )
      return response.data
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['menu-items'] })
      const previousData = queryClient.getQueryData(['menu-items'])

      queryClient.setQueryData<{ data: MenuItem[]; meta: { total: number; page: number; limit: number } }>(['menu-items'], (old) => {
        if (!old) return old
        return {
          ...old,
          data: old.data.map((item) =>
            item.id === id
              ? { ...item, isAvailable: !item.isAvailable }
              : item
          ),
        }
      })

      return { previousData }
    },
    onError: (_err, _id, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['menu-items'], context.previousData)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-items'] })
    },
  })

  // Mutation for bulk updating menu items
  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ ids, data }: { ids: string[]; data: UpdateMenuItemInput }) => {
      if (!selectedBranchId) {
        throw new Error('BranchId is required to bulk update menu items')
      }
      if (!selectedRestaurantId) {
        throw new Error('RestaurantId is required to bulk update menu items')
      }
      const token = await getToken()
      const params = new URLSearchParams()
      if (selectedBranchId) {
        params.append('branchId', selectedBranchId)
      }
      if (selectedRestaurantId) {
        params.append('restaurantId', selectedRestaurantId)
      }
      console.log('[useMenuItems] bulkUpdate request:', {
        ids,
        selectedBranchId,
        selectedRestaurantId,
        url: `/menu-items/bulk-update?${params.toString()}`,
      })
      const response = await apiClient.post<MenuItem[]>(
        `/menu-items/bulk-update?${params.toString()}`,
        { ids, data },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            'X-Branch-ID': selectedBranchId,
            'X-Restaurant-ID': selectedRestaurantId,
          },
        }
      )
      return response.data
    },
    onMutate: async ({ ids, data }) => {
      await queryClient.cancelQueries({ queryKey: ['menu-items'] })
      const previousData = queryClient.getQueryData(['menu-items'])

      queryClient.setQueryData<{ data: MenuItem[]; meta: { total: number; page: number; limit: number } }>(['menu-items'], (old) => {
        if (!old) return old
        return {
          ...old,
          data: old.data.map((item) =>
            ids.includes(item.id)
              ? { ...item, ...data, updatedAt: new Date().toISOString() }
              : item
          ),
        }
      })

      return { previousData }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['menu-items'], context.previousData)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-items'] })
    },
  })

  return {
    menuItems: {
      data: menuItemsQuery.data?.data || [],
      meta: menuItemsQuery.data?.meta,
      isLoading: menuItemsQuery.isLoading,
      error: menuItemsQuery.error,
      refetch: menuItemsQuery.refetch,
    },
    createMenuItem: {
      mutate: createMutation.mutate,
      mutateAsync: createMutation.mutateAsync,
      isLoading: createMutation.isPending,
      error: createMutation.error,
    },
    updateMenuItem: {
      mutate: (id: string, data: UpdateMenuItemInput) => updateMutation.mutate({ id, data }),
      mutateAsync: (id: string, data: UpdateMenuItemInput) => updateMutation.mutateAsync({ id, data }),
      isLoading: updateMutation.isPending,
      error: updateMutation.error,
    },
    deleteMenuItem: {
      mutate: deleteMutation.mutate,
      mutateAsync: deleteMutation.mutateAsync,
      isLoading: deleteMutation.isPending,
      error: deleteMutation.error,
    },
    toggleAvailability: {
      mutate: toggleAvailabilityMutation.mutate,
      mutateAsync: toggleAvailabilityMutation.mutateAsync,
      isLoading: toggleAvailabilityMutation.isPending,
      error: toggleAvailabilityMutation.error,
    },
    bulkUpdate: {
      mutate: bulkUpdateMutation.mutate,
      mutateAsync: bulkUpdateMutation.mutateAsync,
      isLoading: bulkUpdateMutation.isPending,
      error: bulkUpdateMutation.error,
    },
  }
}

/**
 * Public hook to fetch menu items for a branch (no authentication required)
 * Used by customers to view available menu items
 */
export function usePublicMenuItems(branchId: string | null) {
  return useQuery({
    queryKey: ['public-menu-items', branchId],
    queryFn: async () => {
      if (!branchId) {
        throw new Error('Branch ID is required')
      }

      const response = await apiClient.get<{
        success: boolean
        data: MenuItem[]
        meta: { total: number }
      }>(`/api/v1/public/branches/${branchId}/menu-items`)

      return response.data.data
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    retry: 3,
  })
}
